// ChatGPT o4-mini-high
// 2025-06-26
import React, {
  useRef,
  useEffect,
  useCallback,
} from 'react';

interface ZoomPanProps {
  children: React.ReactNode;
  /** Min uniform scale */
  minZoom?: number;
  /** Max uniform scale */
  maxZoom?: number;
  /** Controlled transform. If provided, we use this matrix. */
  matrix?: DOMMatrix;
  /** Called whenever an interaction produces a new matrix. */
  onMatrixChange?: (matrix: DOMMatrix) => void;
}

const ZoomPan: React.FC<ZoomPanProps> = ({
  children,
  minZoom = 0.5,
  maxZoom = 2,
  matrix,
  onMatrixChange,
}) => {
  const gRef = useRef<SVGGElement>(null);
  const isPanning = useRef(false);
  const lastPoint = useRef<DOMPoint|null>(null);
  const matrixRef = useRef<DOMMatrix>(matrix ?? new DOMMatrix());

  // Sync controlled prop → internal + DOM
  useEffect(() => {
    if (matrix) matrixRef.current = matrix;
    applyMatrix(gRef.current!, matrixRef.current, minZoom);
  }, [matrix, minZoom]);

  // —— Zoom around cursor ——  
  const onWheel = useCallback((ev: WheelEvent) => {
    ev.preventDefault();
    const svg = gRef.current!.ownerSVGElement!;
    const vb = svg.viewBox.baseVal;
    const pivot = clientToSvg(ev.clientX, ev.clientY, svg);

    const sign = ev.deltaY < 0 ? 1 : -1;
    const raw = 1 + sign * 0.25;
    const oldS = matrixRef.current.a;
    const newS = Math.min(maxZoom, Math.max(minZoom, oldS * raw));
    const factor = newS / oldS;

    let next = matrixRef.current
      .translate(pivot.x, pivot.y)
      .scale(factor)
      .translate(-pivot.x, -pivot.y);

    next = clampMatrix(next, vb);
    next.a = newS;
    next.d = newS;
    matrixRef.current = next;
    applyMatrix(gRef.current!, next, minZoom);
    onMatrixChange?.(next);
  }, [minZoom, maxZoom, onMatrixChange]);

  // —— Pan move ——  
  const onMouseMove = useCallback((ev: MouseEvent) => {
    if (!isPanning.current)
      return;
    ev.preventDefault();

    const prev = lastPoint.current!;
    const cur  = new DOMPoint(ev.clientX, ev.clientY);
    lastPoint.current = cur;

    // pixel delta
    const dxPx = cur.x - prev.x;
    const dyPx = cur.y - prev.y;

    // map that vector through inverse of the group's CTM
    const ctm = gRef.current!.getCTM()!;
    const inv = ctm.inverse();
    const delta = new DOMPoint(dxPx, dyPx, 0, 0).matrixTransform(inv);

    let next = matrixRef.current.translate(delta.x, delta.y);
    const svg = gRef.current!.ownerSVGElement!;
    next = clampMatrix(next, svg.viewBox.baseVal);

    matrixRef.current = next;
    applyMatrix(gRef.current!, next, minZoom);
    onMatrixChange?.(next);
  }, [minZoom, onMatrixChange]);

  // —— Pan end ——  
  const onMouseUp = useCallback(() => {
    if (!isPanning.current)
      return;
    isPanning.current = false;
    gRef.current!.style.cursor = matrixRef.current.a <= minZoom ? 'zoom-in' : 'grab';
  }, [minZoom]);

  // —— Pan start ——  
  const onMouseDown = useCallback((ev: MouseEvent) => {
    ev.preventDefault();
    isPanning.current = true;
    lastPoint.current = new DOMPoint(ev.clientX, ev.clientY);
    gRef.current!.style.cursor = 'grabbing';
  }, []);

  // Hook up listeners once
  useEffect(() => {
    const g = gRef.current!;
    g.addEventListener('wheel', onWheel, { passive: false });
    g.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      g.removeEventListener('wheel', onWheel);
      g.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  // *eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onWheel, onMouseMove, onMouseDown, onMouseUp]);

  useEffect(() => {
    gRef.current!.style.cursor = 'zoom-in';
  }, []);

  return <g ref={gRef}>
    {children}
  </g>;
};

export default ZoomPan;


// Write a DOMMatrix back onto the <g>
const applyMatrix = (g: SVGGElement, m: DOMMatrix, minZoom: number) => {
  g.setAttribute(
    'transform',
    `matrix(${m.a} ${m.b} ${m.c} ${m.d} ${m.e} ${m.f})`
  );
  g.style.cursor = m.a <= minZoom ? 'zoom-in' : 'grab';
};

// Convert clientX/Y → SVG user-space (for zoom pivot)
const clientToSvg = (x: number, y: number, svg: SVGSVGElement): DOMPoint => {
  const pt = new DOMPoint(x, y);
  return pt.matrixTransform(svg.getScreenCTM()!.inverse());
};

// Clamp e/f so the zoom=1 content box (viewBox) never leaves the view
function clampMatrix (m: DOMMatrix, vb: SVGRect): DOMMatrix {
  const s = m.a; // uniform scale
  let minTx: number, maxTx: number;
  let minTy: number, maxTy: number;

  if (s <= 1) {
    // content box smaller: allow blank but keep content inside view
    minTx = (1 - s) * vb.x;
    maxTx = (1 - s) * (vb.x + vb.width);
    minTy = (1 - s) * vb.y;
    maxTy = (1 - s) * (vb.y + vb.height);
  } else {
    // content box larger: allow pan only within content
    minTx = (1 - s) * (vb.x + vb.width);
    maxTx = (1 - s) * vb.x;
    minTy = (1 - s) * (vb.y + vb.height);
    maxTy = (1 - s) * vb.y;
  }

  // clamp translation
  m.e = Math.min(maxTx, Math.max(minTx, m.e));
  m.f = Math.min(maxTy, Math.max(minTy, m.f));
  return m;
};
